// API operations for ModelDataSampleList component

import { ApiService } from '../../../Services/ApiService';
import { IMsalContext } from '@azure/msal-react';
import { IUnitConfiguration } from '../../../Models/IUnitConfiguration';
import { IDataSample } from '../../../Models/IDataSample';
import { Constants } from '../../../Models/Constants';
import { getFlowTypeUnits, getLocalStorageItem } from '../../../Services/Global';
import { getPageKeyName } from '../../../Utils/UnitConfigurationHelper';
import { COLUMN_WIDTHS } from './ModelDataSampleListTypes';

/**
 * API operations for ModelDataSampleList component
 */
export class ModelDataSampleListApi {
    private apiService: ApiService;
    private unitInputConfiguration: IUnitConfiguration;

    constructor(ctx: IMsalContext, unitInputConfiguration: IUnitConfiguration) {
        this.apiService = new ApiService(ctx);
        this.unitInputConfiguration = unitInputConfiguration;
    }

    /**
     * Get the design session data 
     */
    async getDesignSessionData(
        params: { samplingId: string },
        query?: string, 
        pageNumber?: number, 
        overrideRowsPerPage?: number,
        rowsPerPage: number = 20
    ) {
        const pageKeyName = getPageKeyName(this.unitInputConfiguration, Constants.ModelDataSampleListPage);
        const currentPageNum = pageNumber !== undefined ? pageNumber : Number(getLocalStorageItem(pageKeyName)) || 0;
        
        const configResponse = await this.apiService.getAsync('settings/unitconfiguration', this.unitInputConfiguration);
        const config = await configResponse.json();
        const preferredFlowrateUnits = getFlowTypeUnits(config.flowType);

        let response: Response;
        const effectiveRowsPerPage = overrideRowsPerPage !== undefined ? overrideRowsPerPage : rowsPerPage;
        const pageSize = effectiveRowsPerPage === -1 ? 1000 : effectiveRowsPerPage;
        const pageOffset = effectiveRowsPerPage === -1 ? 0 : currentPageNum * pageSize;
        
        // Extract onlyTodo flag from query
        const hasOnlyTodoParam = query?.includes('onlyTodo=true');
        
        // Check if the configuration has multiple sections 
        if (config.sectionCount > 1) {
            // For multi-section configurations:
            
            // IMPORTANT: For the /all endpoint, the only filter that works is onlyTodo
            // and it must be provided as a query parameter with ? not &
            let url;
            
            // Check if onlyTodo is the only filter or if other filters are present
            if (hasOnlyTodoParam && (!query || query === '&onlyTodo=true')) {
                // If only the onlyTodo filter is present, use the /all endpoint with ?onlyTodo=true
                url = `modeling/datasamples/${params.samplingId}/all?onlyTodo=true`;
                console.log('Using /all endpoint with onlyTodo=true');
            } else if (!query || query === '') {
                // If no filters are present, just use the /all endpoint
                url = `modeling/datasamples/${params.samplingId}/all`;
                console.log('Using /all endpoint with no filters');
            } else {
                // If other filters are present, we have to use the regular endpoint
                // which means pagination will be applied
                url = `modeling/datasamples?pageOffset=${pageOffset}&pageSize=${pageSize}&samplingId=${params.samplingId}${query}`;
                console.log('Using regular endpoint with filters:', query);
            }
            
            console.log('Final API URL:', url);
            response = await this.apiService.getAsync(url, config);
        } else {
            // For single section configuration, use the regular endpoint with pagination
            let url = `modeling/datasamples?pageOffset=${pageOffset}&pageSize=${pageSize}&samplingId=${params.samplingId}`;
            
            // Add any other filter parameters
            if (query) {
                url += query;
                console.log('Adding query parameters to regular endpoint:', query);
            }
            
            console.log('Final API URL (single-section):', url);
            response = await this.apiService.getAsync(url, config);
        }
        
        const data = await response.json();
        const dataSamples: IDataSample[] = data.items;

        let groupedData: { [key: string]: any[] } = {};

        // Group the data samples by section id
        dataSamples.forEach((item: IDataSample) => {
            if (!item.sectionId) {
                return;
            }
            if (!groupedData[item.sectionId]) {
                groupedData[item.sectionId] = [];
            }
            groupedData[item.sectionId].push(item);
        });

        let output: any[] = [];
        // Check if the configuration has multiple sections 
        if (config.isMultiSection === true) {
            for (let j = 0; j < Object.keys(groupedData).length; j++) {
                const key = Object.keys(groupedData)[j];
                const values = groupedData[key];
                values.sort((a, b) => (a.sectionNumber || 0) - (b.sectionNumber || 0));
                let container: any = {
                    id: values[0].id,
                    sectionId: key,
                    dataSampleStatus: values[0].dataSampleStatus,
                }

                // Loop through the data samples and create a new object with the section id and the properties for each section
                for (let i = 0; i < values.length; i++) {
                    const sec: IDataSample = values[i];
                    sec.hotMediaFlowrate && (container[`Sec${sec.sectionNumber}HotMediaFlowrate`] = sec.hotMediaFlowrate);
                    sec.hotMediaPressureDrop && (container[`Sec${sec.sectionNumber}HotMediaPressureDrop`] = sec.hotMediaPressureDrop);
                    sec.hotMediaTempIn && (container[`Sec${sec.sectionNumber}HotMediaTempIn`] = sec.hotMediaTempIn);
                    sec.hotMediaTempOut && (container[`Sec${sec.sectionNumber}HotMediaTempOut`] = sec.hotMediaTempOut);

                    sec.coldMediaFlowrate && (container[`Sec${sec.sectionNumber}ColdMediaFlowrate`] = sec.coldMediaFlowrate);
                    sec.coldMediaPressureDrop && (container[`Sec${sec.sectionNumber}ColdMediaPressureDrop`] = sec.coldMediaPressureDrop);
                    sec.coldMediaTempIn && (container[`Sec${sec.sectionNumber}ColdMediaTempIn`] = sec.coldMediaTempIn);
                    sec.coldMediaTempOut && (container[`Sec${sec.sectionNumber}ColdMediaTempOut`] = sec.coldMediaTempOut);
                }
                output.push(container);
            }
        } else {
            output = dataSamples;
        }

        return {
            data: output,
            config,
            totalRows: data.totalRows,
            currentPageNum,
            effectiveRowsPerPage,
            pageSize,
            preferredFlowrateUnits
        };
    }

    /**
     * Get paginated data samples
     */
    async getPaginatedDataSamples(
        params: { samplingId: string },
        page: number,
        rowsPerPage: number,
        currentFilter: string
    ) {
        const pageSize = rowsPerPage === -1 ? 1000 : rowsPerPage;
        const pageOffset = rowsPerPage === -1 ? 0 : page * rowsPerPage;
        
        const response = await this.apiService.getAsync(
            `modeling/datasamples?pageOffset=${pageOffset}&pageSize=${pageSize}&samplingId=${params.samplingId}${currentFilter}`, 
            this.unitInputConfiguration
        );
        
        const data = await response.json();
        return data.items as IDataSample[];
    }

    /**
     * Helper function to get appropriate column width based on column key
     */
    getColumnWidth = (key: string) => {
        const lowerKey = key.toLowerCase();
        
        if (lowerKey === 'id' || lowerKey === 'row') return COLUMN_WIDTHS.row;
        if (lowerKey === 'status') return COLUMN_WIDTHS.status;
        if (lowerKey === 'comment') return COLUMN_WIDTHS.comment;
        if (lowerKey.includes('temp')) return COLUMN_WIDTHS.temperature;
        if (lowerKey.includes('flowrate')) return COLUMN_WIDTHS.flowrate;
        if (lowerKey.includes('pressure')) return COLUMN_WIDTHS.pressure;
        
        return COLUMN_WIDTHS.default;
    };
} 